<script setup lang="ts">
import { ref, defineProps, onUpdated, onUnmounted, onMounted } from "vue";
import emitter from "@/eventBus";
import { requiredRule } from "@/utilities/requiredRule";
import { usePaymentStore } from "@/stores/payment";
import type { ManagementInformationQuestionsResponse, DropdownQuestion, FreeTextQuestion } from "@/models/graphql/ManagementInformationQuestionsTypes";
import type { VForm } from "vuetify/components";

enum QuestionType {
    Dropdown = "Dropdown",
    FreeText = "FreeText",
}

const paymentStore = usePaymentStore();
const props = defineProps<{
    questions: ManagementInformationQuestionsResponse;
}>();
const formRef = ref<InstanceType<typeof VForm> | null>(null);

// Takes the questions provided in the props and converts them into a format that can be used by the frontend (e.g. sets the modelValue to the default value). Also sorts the questions by priority.
function initialiseQuestions() {
    const dropdownQuestions = props.questions.dropdownQuestions.map((q) => ({
        ...q,
        modelValue: q.defaultValue || null,
        type: QuestionType.Dropdown,
    }));
    const freeTextQuestions = props.questions.freeTextQuestions.map((q) => ({
        ...q,
        modelValue: q.defaultValue || null,
        type: QuestionType.FreeText,
    }));

    paymentStore.miQuestions.questionsAndAnswers = [...dropdownQuestions, ...freeTextQuestions].sort((a, b) => a.priority - b.priority);
}

onUpdated(initialiseQuestions);
initialiseQuestions();

// Triggers the v-form validation to see if any requiredRules are not met. This is needed because the requiredRule is not triggered until the field is touched. Once fields are valid, purchasing can continue.
// @param eventPayload - An object containing the event payload that should be passed through to makePurchase.
const validateForm = async (eventPayload: any) => {
    if (!formRef.value) {
        return;
    }
    const { valid } = await formRef.value.validate();
    if (valid) {
        paymentStore.makePurchase(eventPayload.fulfilmentMethod, eventPayload.bookingId, eventPayload.stripeObject);
    }
};

onMounted(() => {
    // Emitted from payment Store
    emitter.on("miQuestionsCheckAnswerValidity", (eventPayload) => validateForm(eventPayload));
});

onUnmounted(() => {
    emitter.off("miQuestionsCheckAnswerValidity", (eventPayload) => validateForm(eventPayload));
});
</script>

<template>
    <v-container>
        <v-row no-gutter class="mb-4">
            <v-form @submit.prevent ref="formRef" class="w-100">
                <v-col cols="12" v-for="field in paymentStore.miQuestions.questionsAndAnswers" :key="field.id">
                    <span class="required-text-indicator" v-if="field.mandatory">Required</span>
                    <!-- Dropdown Question -->
                    <v-select v-if="field.type === QuestionType.Dropdown" v-model="field.modelValue" :label="(field as DropdownQuestion).question" :items="(field as DropdownQuestion).possibleAnswers" :hint="field.helperText ?? undefined" :persistent-hint="true" :clearable="!field.mandatory" :rules="field.mandatory ? [requiredRule] : []" variant="outlined" bg-color="white"></v-select>
                    <!-- Free Text Question -->
                    <v-text-field v-else-if="field.type === QuestionType.FreeText" v-model="field.modelValue" :label="field.question" :placeholder="(field as FreeTextQuestion).placeholderText || ''" :hint="field.helperText ?? undefined" :persistent-hint="true" :clearable="!field.mandatory" variant="outlined" bg-color="white" :rules="field.mandatory ? [requiredRule] : []"></v-text-field>
                </v-col>
            </v-form>
        </v-row>
    </v-container>
</template>

<style lang="scss" scoped>
:deep(input[type="text"]) {
    padding-inline: var(--v-field-padding-start) var(--v-field-padding-end);
    padding-top: var(--v-field-input-padding-top);
    padding-bottom: var(--v-field-input-padding-bottom);
}
.v-col {
    position: relative;
}
.required-text-indicator {
    display: block;
    color: #b00020;
    font-size: 0.8rem;
    align-self: center;
    white-space: nowrap;
    font-weight: 400;
    position: absolute;
    top: -11px;
    margin: 0;
    right: 18px;
}
</style>
